// src/components/trades/components/RosterTable.js - UPDATED TO USE TRADE COLUMNS
import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DynastyTable } from '../../../services/tableService';
import { dynastyTheme } from '../../../services/colorService';
import { createTradeRosterColumns } from '../../../services/tables/tradeColumns';
import { isPitcherPosition } from '../../../utils/playerClassification';
import { computeTotals } from '../../../services/tables/statTotals';

/**
 * RosterTable Component - TRADE SCREEN VERSION
 * Displays team roster organized by status with collapsible sections
 * Uses NEW tradeColumns.js with narrow 2-row format (season + 14-day stats)
 * 
 * Features:
 * - Groups players by roster status (active, bench, dl, minors)
 * - Collapsible sections for each group
 * - Narrow columns (28-60px) matching myRoster screen styling
 * - Dynamic stat columns based on league settings
 * - 2-row format: Season (white) + Rolling 14-day (yellow)
 * - Clickable rows with background highlight for selection
 * - Draft picks section
 * - Dynasty Theme colors (yellow primary)
 * - Hitters: POS, Name, Team, G, AB, H, [Dynamic Stats], Price, Salary, Contract
 * - Pitchers: POS, Name, Team, G, GS, IP, [Dynamic Stats], Price, Salary, Contract
 */
const RosterTable = ({ 
  players = [],
  picks = [],
  selectedPlayers = [], // Array of MLB IDs that are selected
  selectedPicks = [], // Array of pick_ids that are selected
  onPlayerToggle, // Callback when player clicked
  onPickToggle, // Callback when pick clicked
  positionFilter = 'hitting', // 'hitting' or 'pitching'
  searchTerm = '',
  title = 'Roster',
  league = null // League object with settings
}) => {
  // Collapsible state for draft picks only
  const [picksExpanded, setPicksExpanded] = useState(false);

  // Filter players by position type (batters/pitchers)
  const filteredPlayers = useMemo(() => {
    // Exclude former players (have accrued_info — they're no longer on the team)
    let filtered = players.filter(p => !p.accrued_info);

    // Position filter - batters or pitchers only
    if (positionFilter === 'hitting') {
      filtered = filtered.filter(p => 
        ['C', '1B', '2B', 'SS', '3B', 'OF', 'CF', 'LF', 'RF', 'DH', 'UTIL'].includes(p.info?.position)
      );
    } else if (positionFilter === 'pitching') {
      filtered = filtered.filter(p => 
        isPitcherPosition(p.info?.position)
      );
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.info?.full_name?.toLowerCase().includes(term) ||
        p.info?.first_name?.toLowerCase().includes(term) ||
        p.info?.last_name?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [players, positionFilter, searchTerm]);

  // Build stat configs from league settings
  const buildStatConfigs = (isPitcherList) => {
    // Get league stat categories
    const statCategories = isPitcherList
      ? (league?.league_settings?.pitching_categories || league?.league_settings?.scoring_categories?.pitching || ['W', 'SV', 'K', 'ERA', 'WHIP'])
      : (league?.league_settings?.hitting_categories || league?.league_settings?.scoring_categories?.hitting || ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS']);

    // Stat key mapping
    const statKeyMap = {
      // Batting
      'R': 'runs',
      'HR': 'home_runs',
      'RBI': 'rbi',
      'SB': 'stolen_bases',
      'AVG': 'batting_avg',
      'OBP': 'obp',
      'SLG': 'slg',
      'OPS': 'ops',
      // Pitching
      'W': 'wins',
      'L': 'losses',
      'SV': 'saves',
      'K': 'strikeouts_pitched',
      'ERA': 'era',
      'WHIP': 'whip',
      'QS': 'quality_starts',
      'IP': 'innings_pitched'
    };

    // Build stat configs
    return statCategories.map(statLabel => {
      const statKey = statKeyMap[statLabel] || statLabel.toLowerCase();
      const isDecimal = ['AVG', 'OBP', 'SLG', 'OPS', 'ERA', 'WHIP'].includes(statLabel);
      
      return {
        label: statLabel,
        field: statKey,
        format: isDecimal ? 'float3' : 'default'
      };
    });
  };

  // Position-based sort for default ordering
  const positionSortOrder = { 'C': 1, '1B': 2, '2B': 3, 'SS': 4, '3B': 5, 'OF': 6, 'LF': 6, 'CF': 6, 'RF': 6, 'DH': 7, 'UTIL': 8, 'SP': 9, 'RP': 10, 'P': 11 };
  
  const sortByPosition = (playerList) => {
    return [...playerList].sort((a, b) => {
      const posA = positionSortOrder[a.info?.position] || 99;
      const posB = positionSortOrder[b.info?.position] || 99;
      return posA - posB;
    });
  };

  // Prepare player table data
  const sortedPlayers = sortByPosition(filteredPlayers);
  const isPitcherList = positionFilter === 'pitching';
  const statConfigs = buildStatConfigs(isPitcherList);

  const columns = createTradeRosterColumns({
    statConfigs,
    onPlayerClick: onPlayerToggle,
    selectedPlayers,
    isPitcherList
  });

  // DD-STAT-CONSOLIDATION C3: native sticky TEAM TOTALS row (season window) via the
  // SSoT engine over the exact displayed set. Flat leaf keys match tradeColumns'
  // config.field. Absent → the render shows an honest blank/'-', never a fabricated 0.
  const totalsRow = sortedPlayers.length > 0 ? {
    isTotalsRow: true,
    __stats: {
      [isPitcherList ? 'pitching' : 'hitting']:
        computeTotals(sortedPlayers, { statType: isPitcherList ? 'pitching' : 'hitting', window: 'season' }),
    },
  } : null;

  return (
    <div>
      <h4 className="text-sm font-bold text-neutral-300 mb-3">{title}</h4>
      
      {/* Single Player Table - All Filtered Players */}
      <DynastyTable
        tableType="player-list"
        rowsPerItem={2}
        rowTypes={['season', 'rolling']}
        data={sortedPlayers}
        columns={columns}
        showTotals={!!totalsRow}
        totalsRow={totalsRow}
        emptyMessage={`No ${positionFilter} found`}
        maxHeight="250px"
        onRowClick={onPlayerToggle}
        getRowClassName={(player, rowType) => {
          // Highlight selected players only
          if (player && selectedPlayers.includes(player.ids?.mlb)) {
            return `${dynastyTheme.classes.bg.primaryLight}/20 border-l-2 ${dynastyTheme.classes.border.primary}`;
          }
          return '';
        }}
      />

      {/* Draft Picks Section */}
      {picks && picks.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setPicksExpanded(!picksExpanded)}
            className={`w-full flex items-center justify-between p-3 rounded-lg ${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.transition}`}
            style={{ backgroundColor: picksExpanded ? 'rgb(41, 37, 36)' : 'rgb(28, 25, 23)' }}
          >
            <div className="flex items-center gap-2">
              {picksExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              <span className="font-bold">Draft Picks</span>
              <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>({picks.length})</span>
            </div>
          </button>
          
          {picksExpanded && (
            <div className="mt-2 space-y-2">
              {picks.map(pick => {
                const isSelected = selectedPicks.includes(pick.pick_id);
                const isLocked = pick.is_locked;
                const isML = pick.pick_type === 'ml';

                return (
                  <label
                    key={pick.pick_id}
                    className={`flex items-center gap-3 p-3 rounded-lg ${isLocked ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${dynastyTheme.classes.transition} ${
                      isSelected
                        ? `${dynastyTheme.classes.bg.primaryLight} border ${dynastyTheme.classes.border.primaryBright}`
                        : 'bg-neutral-800 hover:bg-neutral-750'
                    }`}
                    onClick={() => !isLocked && onPickToggle(pick)}
                  >
                    <div className="flex-1">
                      <div className={`font-medium ${isSelected ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.white}`}>
                        {pick.display_label || `${pick.year} ${isML ? 'ML ' : ''}Round ${pick.round}`}
                        {isML && (
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight}`}>
                            ML
                          </span>
                        )}
                        {isLocked && (
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.error}`}>
                            Used
                          </span>
                        )}
                      </div>
                      {pick.original_owner && (
                        <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                          (from {pick.original_owner.team_name})
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RosterTable;
